import { useMemo, useCallback, useRef } from 'react';

import { isEmptyObject } from '@Sage/ssm-utils';

import { hasRangeEndpointValue } from 'components/Filters/rangeValues';
import { useWeb5Context } from 'lib/Web5Context';

import { getEntityOptions, getRelatedEntities } from '../components/Crud/config';
// Subfilters shouldn't be defined as visible filters because are nested to the parent filter
const subFilterIds = {
    callType: 'phonecall',
    listCheckInType: 'checkin',
    videoCallCheckInType: 'videocallcheckin',
    fastCheckIn: 'fastcheckin',
    opportunityCheckIn: 'opportunitycheckin',
    email: 'internetemail',
    type: 'activity',
    videoCallType: 'videocall',
    whatsappType: 'whatsapp',
};

const useForceBIUtils = ({
    standardFieldsSchemaMap,
    extraFieldsSchemaMap,
    rangeFields,
    crossFilterIdSeparator,
    formatDate,
    getEntitiesManager,
}) => {
    const { t, entities: web4Entities, getWeb4EntityFromString } = useWeb5Context();

    const isInitialized = useRef(false);
    const uiFiltersSchemaById = useRef(null);
    const uiFiltersSchemaByFieldConfiguration = useRef(null);

    const entities = useMemo(() => {
        const { COMPANIES, CONTACTS, ACTIVITIES, OPPORTUNITIES, SALESORDERS } = web4Entities;
        return [COMPANIES, CONTACTS, ACTIVITIES, OPPORTUNITIES, SALESORDERS];
    }, [web4Entities]);

    const mainEntity = useMemo(() => {
        const { COMPANIES } = web4Entities;
        return COMPANIES;
    }, [web4Entities]);

    const getMappedFiltersSchemasBy = useCallback(() => {
        let mappedFilterSchemaById = {};
        let mappedFilterSchemaByFieldConfiguration = {};

        entities.forEach((current) => {
            if (!mappedFilterSchemaById?.[current.entity])
                mappedFilterSchemaById[current.entity] = {};
            if (!mappedFilterSchemaByFieldConfiguration?.[current.entity])
                mappedFilterSchemaByFieldConfiguration[current.entity] = {};

            const manager = getEntitiesManager(current.entity);

            const filterSchema = manager.getFilterSchema();

            filterSchema.forEach((filter) => {
                mappedFilterSchemaById[current.entity][filter.id] = filter;
                if (filter.extraFilterProps) {
                    mappedFilterSchemaById[current.entity][filter.extraFilterProps.id] =
                        filter.extraFilterProps;
                }
                mappedFilterSchemaByFieldConfiguration[current.entity][
                    filter.fieldConfiguration || filter.id
                ] = filter;
            });
        });

        uiFiltersSchemaById.current = mappedFilterSchemaById;
        uiFiltersSchemaByFieldConfiguration.current = mappedFilterSchemaByFieldConfiguration;

        isInitialized.current = true;
    }, [entities, getEntitiesManager]);

    const init = useCallback(() => {
        if (!isInitialized.current) {
            getMappedFiltersSchemasBy();
            isInitialized.current = true;
        }
    }, [getMappedFiltersSchemasBy]);

    const getFieldDescription = useCallback(
        ({ entity, fieldId }) => {
            init();
            let fieldConfiguration =
                uiFiltersSchemaByFieldConfiguration.current?.[entity]?.[fieldId] || null;
            if (!fieldConfiguration)
                fieldConfiguration = standardFieldsSchemaMap?.[entity]?.[fieldId] || null;
            if (!fieldConfiguration)
                fieldConfiguration = extraFieldsSchemaMap?.[entity]?.[fieldId] || null;
            return fieldConfiguration?.description || fieldId;
        },
        [extraFieldsSchemaMap, standardFieldsSchemaMap, init],
    );

    const getFiltersErrorMessage = useCallback(
        ({ message, entityFields }) => {
            if (!message || !entityFields || !Object.keys(entityFields).length) return;
            let finalMessage = `${message}`;

            const messages = Object.entries(entityFields).reduce((arr, [key, fields]) => {
                const entity = getWeb4EntityFromString(key);
                let message = `${t(entity.labels.plural)}:`;
                fields.forEach((fieldId, index) => {
                    const description = getFieldDescription({ entity: entity.entity, fieldId });
                    if (index === fields.length - 1) {
                        message = `${message} ${description}.`;
                    } else {
                        message = `${message} ${description},`;
                    }
                });
                arr.push(message);
                return arr;
            }, []);

            if (messages.length) {
                let entitiesMessage = '';
                messages.forEach((current, index) => {
                    if (index === 0) {
                        entitiesMessage = `${current}`;
                    } else {
                        entitiesMessage = ` ${current}`;
                    }
                });
                finalMessage = t(finalMessage, [entitiesMessage]);
            }
            return finalMessage;
        },
        [getFieldDescription, getWeb4EntityFromString, t],
    );

    const getDataType = useCallback(
        (entity, id) => {
            init();
            const newEntity = entity?.extraFieldName ? entity : getWeb4EntityFromString(entity);
            if (!newEntity || !id) return '';
            const entityName = newEntity.extraFieldName;

            let dataType = '';

            if (standardFieldsSchemaMap?.[entityName]?.[id]) {
                dataType = standardFieldsSchemaMap[entityName][id].dataType || '';
            } else if (extraFieldsSchemaMap?.[entityName]?.[id]) {
                dataType = extraFieldsSchemaMap[entityName][id].dataType || '';
            } else if (uiFiltersSchemaById.current?.[newEntity.entity]?.[id]) {
                dataType = uiFiltersSchemaById.current[newEntity.entity][id].dataType || '';
            } else {
                console.error(`This field has no matching datatype: ${id}`);
            }

            return dataType;
        },
        [extraFieldsSchemaMap, getWeb4EntityFromString, init, standardFieldsSchemaMap],
    );

    const formatCompleteValuesForWeb = useCallback(
        ({ entity, id, completeValues, value }) => {
            init();
            const dataType = getDataType(entity, id);
            let newCompleteValues = completeValues;

            if (rangeFields.includes(dataType)) {
                newCompleteValues.value = [null, null];

                if (dataType === 'date') {
                    newCompleteValues = [null, null];
                    if (completeValues.from) newCompleteValues[0] = new Date(completeValues.from);
                    if (completeValues.to) newCompleteValues[1] = new Date(completeValues.to);
                } else {
                    newCompleteValues = ['', ''];
                    if (completeValues.from) newCompleteValues[0] = completeValues.from;
                    if (completeValues.to) newCompleteValues[1] = completeValues.to;
                }
            } else if (dataType === 'bool') {
                newCompleteValues = {};
                newCompleteValues.value = completeValues === true ? '1' : '2';
                newCompleteValues.label =
                    completeValues === true ? t('common_yes') : t('common_no');
            }

            return newCompleteValues;
        },
        [getDataType, init, t, rangeFields],
    );

    const endOfDay = (date = new Date()) => {
        const end = new Date(date);
        end.setHours(23, 59, 59, 999);
        return end;
    };

    const formatValuesForBackend = useCallback(
        ({ entity, id, completeValues }) => {
            init();
            const dataType = getDataType(entity, id);
            let value;

            if (rangeFields.includes(dataType)) {
                value = { from: null, to: null };
                if (hasRangeEndpointValue(completeValues[0])) value.from = completeValues[0];
                else if (hasRangeEndpointValue(completeValues.from))
                    value.from = completeValues.from;
                if (hasRangeEndpointValue(completeValues[1])) value.to = completeValues[1];
                else if (hasRangeEndpointValue(completeValues.to)) value.to = completeValues.to;

                if (dataType === 'date') {
                    if (value.from) {
                        value.from = formatDate(value.from, 'YYYY-MM-DD HH:mm:ss');
                    }
                    if (value.to) {
                        const date = endOfDay(value.to);
                        value.to = formatDate(date, 'YYYY-MM-DD HH:mm:ss');
                    }
                }
            } else if (dataType === 'bool') {
                value = completeValues.value.toString() === '1' ? true : false;
            } else if (Array.isArray(completeValues)) {
                value = completeValues.map((current) => current.value);
            } else if (completeValues?.value) {
                value = [completeValues.value.toString()];
            } else {
                value = completeValues;
            }

            return value;
        },
        [formatDate, getDataType, init, rangeFields],
    );

    /* -->

** Filters structure in FrontEnd:

{
    filters: {[those filters related to the main entity, normally accounts]},
    crossFilters: {
        opportunities: {...}
        ...rest,
    },
    operators: {[those operators related to the main entity, normally accounts]},
    crossOperators: {
        contacts: {...}
        ...rest,
    },
}

** Filters structure in Backend:

{
    filters: {
        accounts: {...},
        contacts: {...},
        opportunities: {...},
        ...rest,
    },
    operators: {
        accounts: {...},
        contacts: {...}
        ...rest,
    }
}

<-- */

    const translateFiltersFromBackend = useCallback(
        ({ filters: backendFilters, operators: backendOperators }) => {
            init();

            let newFilters = {};
            let crossFilters = {};
            let newOperators = {};
            let crossOperators = {};

            if (!isEmptyObject(backendFilters)) {
                Object.entries(backendFilters).forEach(([entity, filters]) => {
                    if (entity === mainEntity.trueName) {
                        Object.entries(filters).forEach(([filterId, filter]) => {
                            const fieldConfiguration =
                                uiFiltersSchemaByFieldConfiguration.current[mainEntity.entity][
                                    filterId
                                ];
                            const id = fieldConfiguration?.id || filterId;

                            newFilters[id] = {
                                completeValues: formatCompleteValuesForWeb({
                                    entity: mainEntity,
                                    id,
                                    completeValues: filter.completeValues,
                                    value: filter.value,
                                }),
                                id,
                            };
                        });
                    } else {
                        const crossEntity = getWeb4EntityFromString(entity);
                        if (!crossFilters[crossEntity.entity])
                            crossFilters[crossEntity.entity] = {};

                        Object.entries(filters).forEach(([filterId, filter]) => {
                            const fieldConfiguration =
                                uiFiltersSchemaByFieldConfiguration.current[crossEntity.entity][
                                    filterId
                                ];
                            const id = fieldConfiguration?.id || filterId;
                            crossFilters[crossEntity.entity][id] = {
                                completeValues: formatCompleteValuesForWeb({
                                    entity: crossEntity,
                                    id,
                                    completeValues: filter.completeValues,
                                    value: filter.value,
                                }),
                                id,
                            };
                        });
                    }
                });
            }

            if (!isEmptyObject(backendOperators)) {
                Object.entries(backendOperators).forEach(([entity, operators]) => {
                    if (entity === mainEntity.trueName) {
                        Object.entries(operators).forEach(([operatorId, operator]) => {
                            const fieldConfiguration =
                                uiFiltersSchemaByFieldConfiguration.current[mainEntity.entity][
                                    operatorId
                                ];
                            const id = fieldConfiguration?.id || operatorId;
                            newOperators[id] = {
                                value: operator.value,
                                extra: fieldConfiguration?.id ? false : true,
                                label: operator.label,
                            };
                        });
                    } else {
                        const crossEntity = getWeb4EntityFromString(entity);
                        if (!crossOperators[crossEntity.entity])
                            crossOperators[crossEntity.entity] = {};

                        Object.entries(operators).forEach(([operatorId, operator]) => {
                            const fieldConfiguration =
                                uiFiltersSchemaByFieldConfiguration.current[crossEntity.entity][
                                    operatorId
                                ];
                            const id = fieldConfiguration?.id || operatorId;
                            crossOperators[crossEntity.entity][id] = {
                                value: operator.value,
                                extra: fieldConfiguration?.id ? false : true,
                                label: operator.label,
                            };
                        });
                    }
                });
            }

            return {
                filters: newFilters,
                operators: newOperators,
                crossFilters,
                crossOperators,
            };
        },
        [formatCompleteValuesForWeb, init, mainEntity, getWeb4EntityFromString],
    );

    const formatCustomFilterValuesForBackend = useCallback((filters) => {
        if (!Object.keys(filters).length) return {};

        return Object.entries(filters).reduce((obj, [key, value]) => {
            if (!value?.completeValues) return obj;

            const completeValues = value.completeValues;
            let finalValue;

            if (Array.isArray(completeValues)) {
                finalValue = completeValues.map((current) => current.value);
            } else {
                finalValue = [completeValues.value];
            }

            obj[key] = { value: finalValue };
            return obj;
        }, {});
    }, []);

    const translateFiltersForBackend = useCallback(
        ({ filters, crossFilters, operators, crossOperators, customFilters }) => {
            init();
            let newFilters = {};

            if (!isEmptyObject(filters)) {
                newFilters[mainEntity.trueName] = Object.entries(filters).reduce(
                    (obj, [filterId, filter]) => {
                        const filterConfig =
                            uiFiltersSchemaById.current[mainEntity.entity][filterId];
                        const id = filterConfig?.fieldConfiguration || filterConfig?.id || filterId;
                        const dataType = getDataType(mainEntity, id);

                        if (dataType) {
                            obj[id] = {
                                value: formatValuesForBackend({
                                    entity: mainEntity,
                                    id,
                                    completeValues: filter.completeValues,
                                }),
                                dataType,
                            };
                        }
                        return obj;
                    },
                    {},
                );
            }

            if (!isEmptyObject(crossFilters)) {
                Object.entries(crossFilters).forEach(([entity, filters]) => {
                    const newEntity = entity?.extraFieldName
                        ? entity
                        : getWeb4EntityFromString(entity);
                    Object.entries(filters).forEach(([filterId, filter]) => {
                        const filterConfig =
                            uiFiltersSchemaById.current[newEntity.entity][filterId];
                        const id = filterConfig?.fieldConfiguration || filterConfig?.id || filterId;

                        const dataType = getDataType(newEntity, id);

                        if (dataType) {
                            if (!newFilters[newEntity.trueName]) {
                                newFilters[newEntity.trueName] = {};
                            }

                            newFilters[newEntity.trueName] = {
                                ...newFilters[newEntity.trueName],
                                [id]: {
                                    value: formatValuesForBackend({
                                        entity: newEntity,
                                        id,
                                        completeValues: filter.completeValues,
                                    }),
                                    dataType,
                                },
                            };
                        }
                    });
                });
            }

            if (customFilters && Object.keys(customFilters).length > 0) {
                Object.entries(customFilters).forEach(([entity, filters]) => {
                    const customs = formatCustomFilterValuesForBackend(filters);
                    newFilters[entity] = {
                        ...newFilters[entity],
                        ...customs,
                    };
                });
            }

            let newOperators = {};

            if (!isEmptyObject(operators)) {
                newOperators[mainEntity.trueName] = Object.entries(operators).reduce(
                    (obj, [operatorId, operator]) => {
                        const filterConfig =
                            uiFiltersSchemaById.current[mainEntity.entity][operatorId];
                        const id =
                            filterConfig?.fieldConfiguration || filterConfig?.id || operatorId;

                        if (!id) return obj;

                        obj[id] = {
                            value: operator.value,
                        };
                        return obj;
                    },
                    {},
                );
            }

            if (!isEmptyObject(crossOperators)) {
                Object.entries(crossOperators).forEach(([entity, operators], index) => {
                    const newEntity = entity?.extraFieldName
                        ? entity
                        : getWeb4EntityFromString(entity);
                    Object.entries(operators).forEach(([operatorId, operator]) => {
                        if (!newOperators[newEntity.trueName]) {
                            newOperators[newEntity.trueName] = {};
                        }
                        const filterConfig =
                            uiFiltersSchemaById.current[newEntity.entity][operatorId];
                        const id =
                            filterConfig?.fieldConfiguration || filterConfig?.id || operatorId;
                        newOperators[newEntity.trueName] = {
                            ...newOperators[newEntity.trueName],
                            [id]: {
                                value: operator.value,
                            },
                        };
                    });
                });
            }

            return {
                filters: newFilters,
                operators: newOperators,
            };
        },
        [
            formatValuesForBackend,
            formatCustomFilterValuesForBackend,
            getDataType,
            getWeb4EntityFromString,
            init,
            mainEntity,
        ],
    );

    // If dashboard filters or operators are applied, they will override the widget filters and operators.
    const overrideDashboardFilters = useCallback(
        ({ dashboardFilters, widgetFilters }) => {
            const { filters: dFilters, operators: dOperators } = dashboardFilters;
            const { filters: wFilters, operators: wOperators } = widgetFilters;

            const filtersObj = entities.reduce((obj, entity) => {
                const trueName = entity.trueName;
                if (!obj?.filters) obj.filters = {};
                if (!obj?.operators) obj.operators = {};

                if (wFilters?.[trueName] || dFilters?.[trueName]) {
                    const entityFilters = {};

                    if (Object.keys(dFilters?.[trueName] || {}).length > 0) {
                        Object.assign(entityFilters, dFilters[trueName] || {});
                    }

                    // Add widget filters, but only if its ID does not exist in dashboard filters or operators
                    if (Object.keys(wFilters?.[trueName] || {}).length > 0) {
                        Object.entries(wFilters[trueName]).forEach(([filterId, filter]) => {
                            const dOperatorExists = dOperators?.[trueName]?.[filterId];
                            const dFilterExists = dFilters?.[trueName]?.[filterId];
                            if (!dOperatorExists && !dFilterExists) {
                                entityFilters[filterId] = filter;
                            }
                        });
                    }

                    if (Object.keys(entityFilters).length > 0) {
                        obj.filters[trueName] = entityFilters;
                    }
                }

                if (wOperators?.[trueName] || dOperators?.[trueName]) {
                    const entityOperators = {};

                    if (Object.keys(dOperators?.[trueName] || {}).length > 0) {
                        Object.assign(entityOperators, dOperators[trueName] || {});
                    }

                    // Add widget operators, but only if its ID does not exist in dashboard filters or operators
                    if (Object.keys(wOperators?.[trueName] || {}).length > 0) {
                        Object.entries(wOperators[trueName]).forEach(([operatorId, operator]) => {
                            const dFilterExists = dFilters?.[trueName]?.[operatorId];
                            const dOperatorExists = dOperators?.[trueName]?.[operatorId];
                            if (!dFilterExists && !dOperatorExists) {
                                entityOperators[operatorId] = operator;
                            }
                        });
                    }

                    if (Object.keys(entityOperators).length > 0) {
                        obj.operators[trueName] = entityOperators;
                    }
                }

                return obj;
            }, {});

            return filtersObj;
        },
        [entities],
    );

    const processDataToSendFromWidget = useCallback(
        ({ data, dashboardFilters, customFilters }) => {
            let dFilters;
            let dOperators;

            const translated = translateFiltersForBackend({ ...dashboardFilters, customFilters });
            dFilters = translated.filters;
            dOperators = translated.operators;

            const { filters, crossFilters, operators, crossOperators, ...restData } = data;

            const { filters: wFilters, operators: wOperators } = translateFiltersForBackend({
                filters,
                crossFilters,
                operators,
                crossOperators,
            });

            const finalDashboardFilters = { filters: dFilters, operators: dOperators };
            const widgetFilters = { filters: wFilters, operators: wOperators };

            const newFilters = overrideDashboardFilters({
                dashboardFilters: finalDashboardFilters,
                widgetFilters,
            });

            const newData = {
                ...restData,
                filters: newFilters.filters,
                operators: newFilters.operators,
            };

            return newData;
        },
        [overrideDashboardFilters, translateFiltersForBackend],
    );

    // xAxisEntity and xAxisBreakdownEntity are not needed to be sent to backend
    // because they are just to make the CRUD more understandable for the user.
    const removeIgnorableFields = useCallback((data) => {
        const newData = { ...data };
        delete newData.xAxisEntity;
        delete newData.xAxisBreackdownEntity;
        return newData;
    }, []);

    const processDataToSendFromCrud = useCallback(
        ({ data }) => {
            const {
                filters: dataFilters,
                crossFilters: dataCrossFilters,
                operators: dataOperators,
                crossOperators: dataCrossOperators,
                ...restData
            } = data;

            let filtersObj = {
                filters: dataFilters,
                crossFilters: dataCrossFilters,
                operators: dataOperators,
                crossOperators: dataCrossOperators,
            };

            const { filters: newFilters, operators: newOperators } = translateFiltersForBackend({
                ...filtersObj,
            });

            const newData = removeIgnorableFields(restData);

            return { ...newData, filters: newFilters, operators: newOperators };
        },
        [translateFiltersForBackend, removeIgnorableFields],
    );

    // This method returns unique filters (joining filters and operators)
    const getUniqueFilters = useCallback(
        ({ filters, crossFilters, operators, crossOperators }) => {
            init();

            let uiFilters = [];
            let uiFilterNames = [];

            if (!isEmptyObject(filters)) {
                Object.entries(filters).forEach(([filterId, filter]) => {
                    let fieldConfiguration =
                        uiFiltersSchemaById.current?.[mainEntity.entity]?.[filterId];
                    if (!fieldConfiguration) {
                        fieldConfiguration =
                            extraFieldsSchemaMap?.[mainEntity.extraFieldName]?.[filterId];
                    }

                    if (!fieldConfiguration) {
                        console.error(
                            `This filter has not matching fieldConfiguration: ${filterId}`,
                        );
                        return;
                    }

                    const id = filterId;

                    uiFilters.push({
                        entityLabel: mainEntity.labels.plural,
                        id,
                        fullId: id,
                        description: fieldConfiguration?.description || t(fieldConfiguration.label),
                    });
                    uiFilterNames.push(id);
                });
            }

            if (!isEmptyObject(crossFilters)) {
                Object.entries(crossFilters).forEach(([entity, newFilters]) => {
                    if (Object.keys(newFilters).length) {
                        Object.entries(newFilters).forEach(([filterId, filter]) => {
                            const filterEntity = getWeb4EntityFromString(entity);
                            let fieldConfiguration =
                                uiFiltersSchemaById.current?.[filterEntity.entity]?.[filterId];
                            if (!fieldConfiguration) {
                                fieldConfiguration =
                                    extraFieldsSchemaMap?.[filterEntity.extraFieldName]?.[filterId];
                            }

                            if (!fieldConfiguration) {
                                console.error(
                                    `This crossFilter has not matching fieldConfiguration: ${filterId}`,
                                );
                                return;
                            }

                            const id = filterId;

                            const fullUiId = `${filterEntity.entity}${crossFilterIdSeparator}${id}`;

                            uiFilters.push({
                                entityLabel: filterEntity.labels.plural,
                                id,
                                fullId: fullUiId,
                                description:
                                    fieldConfiguration?.description || t(fieldConfiguration?.label),
                            });
                            uiFilterNames.push(fullUiId);
                        });
                    }
                });
            }

            if (!isEmptyObject(operators)) {
                Object.entries(operators).forEach(([operatorId, operator]) => {
                    let fieldConfiguration =
                        uiFiltersSchemaById.current?.[mainEntity.entity]?.[operatorId];
                    if (!fieldConfiguration) {
                        fieldConfiguration =
                            extraFieldsSchemaMap?.[mainEntity.extraFieldName]?.[operatorId];
                    }

                    if (!fieldConfiguration) {
                        console.error(
                            `This operator has not matching fieldConfiguration: ${operatorId}`,
                        );
                        return;
                    }

                    const id = operatorId;

                    if (!uiFilterNames.includes(id)) {
                        uiFilters.push({
                            entityLabel: mainEntity.labels.plural,
                            id,
                            fullId: id,
                            description:
                                fieldConfiguration?.description || t(fieldConfiguration.label),
                        });
                        uiFilterNames.push(id);
                    }
                });
            }

            if (!isEmptyObject(crossOperators)) {
                Object.entries(crossOperators).forEach(([entity, newOperators]) => {
                    if (Object.keys(newOperators).length) {
                        Object.entries(newOperators).forEach(([operatorId, operator]) => {
                            const operatorEntity = getWeb4EntityFromString(entity);
                            let fieldConfiguration =
                                uiFiltersSchemaById.current?.[operatorEntity.entity]?.[operatorId];
                            if (!fieldConfiguration) {
                                fieldConfiguration =
                                    extraFieldsSchemaMap?.[operatorEntity.extraFieldName]?.[
                                        operatorId
                                    ];
                            }

                            if (!fieldConfiguration) {
                                console.error(
                                    `This crossOperator has not matching field configuration: ${operatorId}`,
                                );
                                return;
                            }

                            const id = operatorId;
                            const fullUiId = `${operatorEntity.entity}${crossFilterIdSeparator}${id}`;

                            if (!uiFilterNames.includes(fullUiId)) {
                                uiFilters.push({
                                    entityLabel: operatorEntity.labels.plural,
                                    id,
                                    fullId: fullUiId,
                                    description:
                                        fieldConfiguration?.description ||
                                        t(fieldConfiguration.label),
                                });
                                uiFilterNames.push(fullUiId);
                            }
                        });
                    }
                });
            }

            return { uiFilters, uiFilterNames };
        },
        [
            crossFilterIdSeparator,
            extraFieldsSchemaMap,
            getWeb4EntityFromString,
            init,
            mainEntity,
            t,
        ],
    );

    const processFiltersToInitDashboard = useCallback(
        ({ filters: dFilters, operators: dOperators }) => {
            const { filters, crossFilters, operators, crossOperators } =
                translateFiltersFromBackend({
                    filters: dFilters,
                    operators: dOperators,
                });

            return {
                filters,
                crossFilters,
                operators,
                crossOperators,
            };
        },
        [translateFiltersFromBackend],
    );

    const processFiltersToInitCrud = useCallback(
        ({ filters, crossFilters, operators, crossOperators }) => {
            const uniqueFilters = getUniqueFilters({
                filters,
                crossFilters,
                operators,
                crossOperators,
            });

            let visibleFilters = [];
            let openFilters = {};

            if (uniqueFilters.uiFilters?.length) {
                visibleFilters = uniqueFilters.uiFilters.reduce((arr, current) => {
                    // Subfilters shouldn't be defined as visible filters because are nested to the parent filter
                    // In web4 this is not necessary to be done because a subfilter is never defined as a visible filter
                    // because is nested to the parent filter, but in ForceBI widget CRUD since we get the visible filters
                    // from the filters stored in the widget, we need to do it manually
                    if (subFilterIds[current.id]) return arr;
                    openFilters[current.fullId] = true;
                    arr.push(current.fullId);
                    return arr;
                }, []);
            }

            return {
                filters,
                crossFilters,
                operators,
                crossOperators,
                visibleFilters,
                openFilters,
            };
        },
        [getUniqueFilters],
    );

    const getEntityNameFromValue = useCallback((value) => {
        return value?.split?.('.')?.[0] || '';
    }, []);

    // We use this method to populate xAxisEntity and xAxisBreakdownEntity
    // Those fields do not exist in backend but are necessary to make the CRUD more understandable for the user
    const processDataToInitCrud = useCallback(
        (data) => {
            const options = getEntityOptions({ t });
            const entityFields = {
                xAxis: 'xAxisEntity',
                xAxisBreakdown: 'xAxisBreakdownEntity',
            };
            let entityName = '';
            const newData = { ...data };

            // Some values are from a related entity, we need to map them to the main entity
            const relatedEntities = getRelatedEntities();
            const reversedRelatedEntities = Object.entries(relatedEntities).reduce(
                (result, [key, values]) => {
                    values.forEach((value) => {
                        result[value] = key;
                    });
                    return result;
                },
                {},
            );

            Object.entries(entityFields).forEach(([key, value]) => {
                if (newData?.[key]?.value) {
                    entityName = getEntityNameFromValue(newData[key].value);
                    if (reversedRelatedEntities[entityName]) {
                        entityName = reversedRelatedEntities[entityName];
                    }

                    const entity = options.find((option) => option.value === entityName);
                    if (entity) {
                        newData[value] = entity;
                    } else {
                        newData[value] = null;
                    }
                }
            });

            return newData;
        },
        [getEntityNameFromValue, t],
    );

    return {
        processFiltersToInitDashboard,
        processFiltersToInitCrud,
        processDataToSendFromWidget,
        processDataToSendFromCrud,
        processDataToInitCrud,
        translateFiltersForBackend,
        translateFiltersFromBackend,
        getUniqueFilters,
        entities,
        entity: mainEntity,
        getFiltersErrorMessage,
    };
};

export default useForceBIUtils;
